import React, { useState, useMemo } from "react";
import {
  Alert,
  Container,
  Row,
  Col,
  Card,
  Form,
  InputGroup,
} from "react-bootstrap";
import { useAuth } from "../AuthContext";
import "../App.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  PersonBadge,
  Cash,
  Tree,
  Journals,
  Stack,
  Filter,
  Calendar3,
  PCircle,
  Stripe,
} from "react-bootstrap-icons";

const Dashboard = ({ date, tema, partizi }) => {
  const { user } = useAuth();

  // --- 1. STATE-URI ---
  const [filtruAn, setFiltruAn] = useState("Toti");
  const [filtruOcol, setFiltruOcol] = useState("Toate");
  const [filtruProprietar, setFiltruProprietar] = useState("Toti");
  const [filtruTip, setFiltruTip] = useState("Toate");
  const [filtruStoc, setFiltruStoc] = useState("Toate");
  const [showSalut, setShowSalut] = useState(true);

  // --- 2. LOGICĂ FILTRARE ---

  // A. Filtrare pe baza rolului (Securitate)
  const datePermise = useMemo(() => {
    if (!date) return [];
    return date.filter((item) => {
      if (user?.rol === "administrator") return true;
      return (
        item.ocol?.toString().trim().toLowerCase() ===
        user?.ocol?.toString().trim().toLowerCase()
      );
    });
  }, [date, user]);

  // B. Filtrare Globală (Interfață)
  const dateFiltrateGlobal = useMemo(() => {
    return datePermise.filter((item) => {
      // 1. Calcul an
     const dataRaw = item.dataCreare;
     let dataObiect;
     if (dataRaw?.seconds) {
       dataObiect = new Date(dataRaw.seconds * 1000);
     } else if (dataRaw) {
       dataObiect = new Date(dataRaw);
     } else {
       dataObiect = new Date(); // Fallback
     }
      const anContract = dataObiect.getFullYear().toString();

      // 2. Logica filtrare STOC
      let conditieStoc = true;
      if (filtruStoc !== "Toate") {
        const ids =
          item.partiziAferente || (item.partidaId ? [item.partidaId] : []);
        conditieStoc = ids.some((idCautat) => {
          const p = partizi?.find((x) => String(x.id) === String(idCautat));
          return (
            p?.stoc?.toString().toLowerCase().trim() ===
            filtruStoc.toLowerCase()
          );
        });
      }

      return (
        conditieStoc &&
        (filtruAn === "Toti" || anContract === filtruAn) &&
        (filtruOcol === "Toate" || item.ocol === filtruOcol) &&
        (filtruTip === "Toate" ||
          filtruTip === "Toti" ||
          item.tip === filtruTip) &&
        (filtruProprietar === "Toti" || item.proprietar === filtruProprietar)
      );
    });
  }, [
    datePermise,
    filtruAn,
    filtruOcol,
    filtruTip,
    filtruProprietar,
    filtruStoc,
    partizi,
  ]);

  // --- 3. CALCULE METRICI ---

  const totalVolum = useMemo(
    () =>
      dateFiltrateGlobal.reduce(
        (acc, curr) => acc + (parseFloat(curr.volum) || 0),
        0,
      ),
    [dateFiltrateGlobal],
  );
  const totalValoare = useMemo(
    () =>
      dateFiltrateGlobal.reduce(
        (acc, curr) => acc + (parseFloat(curr.valoare) || 0),
        0,
      ),
    [dateFiltrateGlobal],
  );
  const nrContracte = dateFiltrateGlobal.length;

  const stocDinamic = useMemo(() => {
    if (!partizi) return 0;
    const partiziUniceProcesate = new Set();
    let sumaStoc = 0;

    dateFiltrateGlobal.forEach((contract) => {
      const ids =
        contract.partiziAferente ||
        (contract.partidaId ? [contract.partidaId] : []);
      ids.forEach((id) => {
        if (!partiziUniceProcesate.has(String(id))) {
          const p = partizi.find((x) => String(x.id) === String(id));
          if (p?.stoc?.toString().toLowerCase().trim() === "da") {
            sumaStoc += parseFloat(p.volBrut || 0);
          }
          partiziUniceProcesate.add(String(id));
        }
      });
    });
    return sumaStoc;
  }, [dateFiltrateGlobal, partizi]);

  // --- 4. CONFIGURAȚII GRAFICE ---
  const CULORI_PIE = [
    "#A3C9A8",
    "#D4B483",
    "#8EACCD",
    "#E6A5A5",
    "#C8B4BA",
    "#F7D794",
  ];
  const proprietari = ["Stat", "UAT", "AD"];

  const genereazaDateNaturaProdus = () => {
    const harta = {};
    dateFiltrateGlobal.forEach((curr) => {
      const idPartida = curr.partiziAferente?.[0] || curr.partidaId;
      const p = partizi?.find((x) => String(x.id) === String(idPartida));
      const natura = p?.naturaProdus || p?.natura || "Nespecificată";
      harta[natura] = (harta[natura] || 0) + (parseFloat(curr.volum) || 0);
    });
    return Object.keys(harta).map((key) => ({
      tip: key,
      volum: parseFloat(harta[key].toFixed(2)),
    }));
  };



  if (!date || date.length === 0)
    return <p className="p-3 text-center">Se încarcă datele...</p>;

  return (
    <Container fluid className="py-2">
      <Alert
        show={showSalut}
        variant="info"
        onClose={() => setShowSalut(false)}
        dismissible
        transition
        className="shadow-sm border-0 d-flex justify-content-center align-items-center text-center"
        style={{
          backgroundColor: "var(--fundal-sectiune)",
          color: "var(--text-principal)",
          borderTop: "4px solid #0dcaf0",
          transition: "all 0.3s ease",
        }}
      >
        <PersonBadge size={30} className="mb-2 text-info" />

        <div>
          Salut, <strong>{user?.nume}</strong>! Ești logat ca{" "}
          <strong>{user?.rol}</strong> și ai atribuții pentru{" "}
          {user?.rol === "administrator" ? (
            <span className="fw-bold text-success">
              toate reperele din Direcția Silvică
            </span>
          ) : (
            <>
              Ocolul Silvic <strong>{user?.ocol}</strong>
            </>
          )}
        </div>
      </Alert>

      {/* CARD FILTRE */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Row className="align-items-end">
            {/* Filtru an */}
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Label className="small fw-bold">An</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Calendar3 size={14} />
                </InputGroup.Text>
                <Form.Select
                  size="sm"
                  value={filtruAn}
                  onChange={(e) => setFiltruAn(e.target.value)}
                >
                  <option value="Toti">Toți Anii</option>
                  {[
                    ...new Set(
                      datePermise.map((d) =>
                        new Date(
                          d.dataCreare?.seconds
                            ? d.dataCreare.seconds * 1000
                            : d.dataCreare,
                        ).getFullYear(),
                      ),
                    ),
                  ]
                    .sort((a, b) => b - a)
                    .map((an) => (
                      <option key={an} value={an}>
                        {an}
                      </option>
                    ))}
                </Form.Select>
              </InputGroup>
            </Col>
            {/* Filtru proprietar */}
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Label className="small fw-bold">Proprietar</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <PCircle size={14} />
                </InputGroup.Text>
                <Form.Select
                  size="sm"
                  value={filtruProprietar}
                  onChange={(e) => setFiltruProprietar(e.target.value)}
                >
                  <option value="Toti">Toți</option>
                  {proprietari.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
            {/* Filtru tip contract */}
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Label className="small fw-bold">Tip Contract</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Journals size={14} />
                </InputGroup.Text>
                <Form.Select
                  size="sm"
                  value={filtruTip}
                  onChange={(e) => setFiltruTip(e.target.value)}
                >
                  <option value="Toate">Toate</option>
                  <option value="Prestari">Prestări</option>
                  <option value="Regie">Regie</option>
                </Form.Select>
              </InputGroup>
            </Col>
            {/*Filtru stoc*/}
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Label className="small fw-bold">Stoc</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Stripe size={14} />
                </InputGroup.Text>
                <Form.Select
                  size="sm"
                  value={filtruStoc}
                  onChange={(e) => setFiltruStoc(e.target.value)}
                >
                  <option value="Toate">Tot</option>
                  <option value="da">Cu Stoc (Da)</option>
                  <option value="nu">Fără Stoc (Nu)</option>
                </Form.Select>
              </InputGroup>
            </Col>
            {/* Filtru ocol - doar pentru admin */}
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Label className="small fw-bold">Ocol Silvic</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Tree size={14} />
                </InputGroup.Text>
                <Form.Select
                  size="sm"
                  value={
                    user?.rol === "administrator" ? filtruOcol : user?.ocol
                  }
                  onChange={(e) => setFiltruOcol(e.target.value)}
                  disabled={user?.rol !== "administrator"}
                >
                  <option value="Toate">Toate Ocoalele</option>
                  {[...new Set(datePermise.map((d) => d.ocol))]
                    .sort()
                    .map((oc) => (
                      <option key={oc} value={oc}>
                        {oc}
                      </option>
                    ))}
                </Form.Select>
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* DASHBOARD CONTENT */}
      <Row>
        {/* KPI-uri pe coloană */}
        <Col lg={4} className="order-lg-2">
          <Row className="g-3 mb-4">
            <MetricCard
              title="Volum Total"
              value={totalVolum}
              unit="mc"
              icon={<Stack />}
              colorClass="card-total"
            />
            <MetricCard
              title="Stoc"
              value={stocDinamic}
              unit="mc"
              icon={<Filter />}
              colorClass="card-stoc-verde"
            />
            <MetricCard
              title="Valoare"
              value={totalValoare}
              unit="RON"
              icon={<Cash />}
              colorClass="card-valoare"
            />
            <MetricCard
              title="Contracte"
              value={nrContracte}
              unit=""
              icon={<Journals />}
              colorClass="card-contracte"
            />
          </Row>
        </Col>

        {/* Grafic Pie */}
        <Col lg={8} className="order-lg-1">
          <Card className="shadow-sm border-0 mb-4 p-3">
            <h5 style={{ color: "#2e7d32" }}>Natură Produs</h5>
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genereazaDateNaturaProdus()}
                    dataKey="volum"
                    nameKey="tip"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={60}
                    paddingAngle={3}
                  >
                    {genereazaDateNaturaProdus().map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CULORI_PIE[index % CULORI_PIE.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [value.toFixed(2) + " mc", "Volum"]}
                    contentStyle={{
                      backgroundColor: "var(--fundal-sidebar)",
                      color: "var(--text-principal)",
                      border: `1px solid var(--bordura)`,
                      borderRadius: "8px",
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => {
                      const volum = entry.payload.volum;

                      return (
                        <span
                          style={{
                            fontSize: "12px",
                            marginLeft: "5px",
                          }}
                        >
                          {value}: <strong>{volum.toFixed(2)} mc</strong>
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* --- SECȚIUNE GRAFIC DINAMIC PE OCOALE --- */}
      <Card className="shadow-sm border-0 mb-4 p-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h5 style={{ color: "#78987c", margin: 0 }}>
            Distribuție Volum pe Ocoale Silvice
          </h5>
          <span className="badge bg-light text-dark border mt-2 mt-md-0">
            Filtru curent:{" "}
            {filtruProprietar === "Toti"
              ? "Toți Proprietarii"
              : `Proprietar ${filtruProprietar}`}
          </span>
        </div>

        <div style={{ height: 350 }}>
          {(() => {
            // Generăm datele pentru grafic folosind variabila de stare filtruProprietar
            // dateFiltrateGlobal conține deja filtrarea de An, Tip Contract și Ocol
            const dateGraficDinamice = dateFiltrateGlobal
              .reduce((acc, curr) => {
                const ocolNume = curr.ocol || "Nespecificat";
                const volumNumeric = parseFloat(curr.volum) || 0;

                const existent = acc.find((item) => item.ocol === ocolNume);
                if (existent) {
                  existent.volum = parseFloat(
                    (existent.volum + volumNumeric).toFixed(2),
                  );
                } else {
                  acc.push({ ocol: ocolNume, volum: volumNumeric });
                }
                return acc;
              }, [])
              .sort((a, b) => b.volum - a.volum); // Sortăm descrescător pentru un aspect curat

            return dateGraficDinamice.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dateGraficDinamice}
                  margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="var(--bordura)"
                  />
                  <XAxis
                    dataKey="ocol"
                    tick={{ fontSize: 11, fill: "var(--text-principal)" }}
                    angle={-45}
                    textAnchor="end"
                    interval={0} // Forțează afișarea tuturor numelor de ocoale
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "var(--text-principal)" }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.05)" }}
                    contentStyle={{
                      backgroundColor: "var(--fundal-sidebar)",
                      border: "1px solid var(--bordura)",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="volum"
                    fill="#8ba98f"
                    radius={[4, 4, 0, 0]}
                    barSize={45}
                    name="Volum (mc)"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex h-100 align-items-center justify-content-center text-muted border rounded bg-light">
                Nu există date pentru selecția curentă (Proprietar:{" "}
                {filtruProprietar})
              </div>
            );
          })()}
        </div>
      </Card>
    </Container>
  );
};

// Sub-componentă pentru curățenia codului
const MetricCard = ({ title, value, unit, icon, colorClass }) => (
  <Col xs={12}>
    <Card className={`${colorClass} shadow-sm border-0 text-white`}>
      <Card.Body className="d-flex align-items-center p-3">
        <div className="me-3">{React.cloneElement(icon, { size: 30 })}</div>
        <div>
          <div className="small text-uppercase opacity-75">{title}</div>
          <h4 className="mb-0">
            {value.toLocaleString("ro-RO")} <small>{unit}</small>
          </h4>
        </div>
      </Card.Body>
    </Card>
  </Col>
);

export default Dashboard;
